import { err, ok } from '@src/services/common/Result';
import {
  clearSpeakerCache,
  injectedOverrides,
} from '@src/stores-v2/speakerCache';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  carriesSpeakerLabel,
  convertRange,
  restoreRange,
  withTrailingMarks,
} from '@src/utility/speakerRelabel';
import {
  clearSpeakerCache as clearSpeakerUtilsCache,
  extractSpeakerLabelsFromTranscriptElements,
  fetchSpeakerVoiceMappings,
  getSpeakerColorFromLabel,
  getSpeakerNameFromLabel,
  SPEAKER_COLORS,
  updateSpeakerVoiceMappings,
} from '@src/utility/speakerUtils';

import type {
  DatoSpeaker,
  SpeakerGender,
  SpeakerRepository,
  SpeakerStoryRef,
} from '@src/repositories/SpeakerRepository';
import type { Result } from '@src/services/common/Result';
import type { TranscriptElement } from '@src/types.ts/transcript';

const logger = LogManager.getLogger('service:SpeakerManagementService');

export function getSpeakerColor(speakerId: string, index: number): string {
  let hash = 0;
  for (let i = 0; i < speakerId.length; i++) {
    hash = speakerId.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % SPEAKER_COLORS.length;
  return (
    SPEAKER_COLORS[colorIndex] || SPEAKER_COLORS[index % SPEAKER_COLORS.length]
  );
}

export interface SpeakerWithColor extends DatoSpeaker {
  color: string;
}

export interface SpeakerManagementServiceDeps {
  speakerRepository: SpeakerRepository;
}

export interface FetchSpeakersResult {
  storySpeakers: SpeakerWithColor[];
  orgSpeakers: SpeakerWithColor[];
  mappings: Record<string, string>;
}

export const OVERRIDE_LABEL_PREFIX = 'override_';

export interface AssignedSpeakersResult {
  /** Story speakers attached to the transcript by a mapping OR a section override. */
  assigned: SpeakerWithColor[];
  /** Story speakers attached by neither mechanism. */
  unassigned: SpeakerWithColor[];
  /**
   * Every transcript label that resolves to a speaker → that speaker's DatoCMS
   * id: the `spk_X` voice mappings plus one `override_<datoId>` entry per
   * section override. Keyed the way the dub manifest keys its segments, so a
   * request map built from it covers override passages too.
   */
  labelToSpeakerId: Record<string, string>;
  /** The `override_<datoId>` labels currently present on the transcript. */
  overrideLabels: string[];
}

/**
 * The one definition of "assigned" for a story's speakers (PLAT-11583).
 *
 * A speaker is attached to a transcript in two ways: a voice mapping
 * (`spk_X → datoId`) or a section override (`override_<datoId>` written on the
 * passage's elements). Read overrides from `originalTranscription.elements`
 * (where they are written) AND from the injected-overrides cache, because in
 * Content Studio a fresh section assignment lands on the scoped editor and only
 * reaches the global transcription store after a debounced save.
 *
 * Both the Speakers panel and the dub modal build their lists from this, so a
 * speaker cannot be Assigned on one surface and missing from the other.
 */
export function resolveAssignedSpeakers(input: {
  storyId: string;
  storySpeakers: SpeakerWithColor[];
  mappings: Record<string, string>;
  transcriptElements: TranscriptElement[];
}): AssignedSpeakersResult {
  const { storyId, storySpeakers, mappings, transcriptElements } = input;

  const overrideLabelSet = new Set(
    extractSpeakerLabelsFromTranscriptElements(transcriptElements).filter(
      label => label.startsWith(OVERRIDE_LABEL_PREFIX),
    ),
  );
  const injected = injectedOverrides.get(storyId);
  if (injected) {
    for (const label of injected.keys()) {
      if (label.startsWith(OVERRIDE_LABEL_PREFIX)) overrideLabelSet.add(label);
    }
  }
  const overrideLabels = Array.from(overrideLabelSet);

  const labelToSpeakerId: Record<string, string> = {};
  for (const [label, datoId] of Object.entries(mappings)) {
    if (datoId) labelToSpeakerId[label] = datoId;
  }
  for (const label of overrideLabels) {
    const datoId = label.slice(OVERRIDE_LABEL_PREFIX.length);
    if (datoId) labelToSpeakerId[label] = datoId;
  }

  const assignedIds = new Set(Object.values(labelToSpeakerId));
  return {
    assigned: storySpeakers.filter(s => assignedIds.has(s.id)),
    unassigned: storySpeakers.filter(s => !assignedIds.has(s.id)),
    labelToSpeakerId,
    overrideLabels,
  };
}

/** A transcript voice (spk_X) with no Dato speaker mapped to it yet. */
export interface AnonymousSpeaker {
  speakerLabel: string; // e.g., "spk_0"
  name: string; // e.g., "Speaker 1"
  color: string;
}

/**
 * The transcript's real speaker labels, merged across `elementLists` (section
 * `override_*` labels are not transcript speakers and are dropped), and which of
 * them no Dato speaker is mapped to yet.
 *
 * Both the Speakers panel and the post-upload identification prompt build
 * "who still needs a name" from this, so the two cannot disagree.
 */
export function resolveTranscriptSpeakers(input: {
  elementLists: TranscriptElement[][];
  mappings: Record<string, string>;
}): { speakerLabels: string[]; anonymous: AnonymousSpeaker[] } {
  const labelSet = new Set<string>();
  for (const elements of input.elementLists) {
    for (const label of extractSpeakerLabelsFromTranscriptElements(elements)) {
      labelSet.add(label);
    }
  }
  const speakerLabels = Array.from(labelSet).filter(
    label => !label.startsWith(OVERRIDE_LABEL_PREFIX),
  );
  const anonymous = speakerLabels
    .filter(label => !input.mappings[label])
    .map(label => ({
      speakerLabel: label,
      name: getSpeakerNameFromLabel(label),
      color: getSpeakerColorFromLabel(label),
    }));
  return { speakerLabels, anonymous };
}

export interface SpeakerData {
  name: string;
  title?: string;
  bio?: string;
  voice?: string;
  /** '' clears the field back to unset (PLAT-11692). */
  gender?: SpeakerGender | '';
  headshots?: Array<{ id: string; url: string }> | null;
}

export interface OrgSpeakersResult {
  speakers: DatoSpeaker[];
  usage: Record<string, SpeakerStoryRef[]>;
}

/**
 * Converts UI-shaped speaker data into the format expected by the repository
 * (DatoCMS).  Headshots arrive as `{ id, url }` from the form but the repo
 * expects `{ upload_id }`.  All other fields pass through unchanged.
 */
function toRepoSpeakerData(data: SpeakerData): {
  name?: string;
  title?: string;
  bio?: string;
  voice?: string;
  gender?: SpeakerGender | '' | null;
  headshots?: Array<{ upload_id: string }> | null;
} {
  const repoData: {
    name?: string;
    title?: string;
    bio?: string;
    voice?: string;
    gender?: SpeakerGender | '' | null;
    headshots?: Array<{ upload_id: string }> | null;
  } = {
    name: data.name,
    title: data.title,
    bio: data.bio,
    voice: data.voice,
    gender: data.gender,
  };
  if (data.headshots !== undefined) {
    repoData.headshots = data.headshots
      ? data.headshots.map(h => ({ upload_id: h.id }))
      : null;
  }
  return repoData;
}

export class SpeakerManagementService {
  private readonly speakerRepository: SpeakerRepository;

  constructor(deps: SpeakerManagementServiceDeps) {
    this.speakerRepository = deps.speakerRepository;
  }

  /**
   * Fetch story/org speakers and voice mappings for a story.
   */
  async fetchSpeakersForStory(
    storyId: string,
  ): Promise<Result<FetchSpeakersResult>> {
    try {
      const [{ storySpeakers, albumSpeakers }, fetchedMappings] =
        await Promise.all([
          this.speakerRepository.getStorySpeakers(storyId),
          fetchSpeakerVoiceMappings(storyId),
        ]);

      const storyWithColors = storySpeakers.map((s, i) => ({
        ...s,
        color: getSpeakerColor(s.id, i),
      }));
      const orgWithColors = albumSpeakers.map((s, i) => ({
        ...s,
        color: getSpeakerColor(s.id, i),
      }));

      return ok({
        storySpeakers: storyWithColors,
        orgSpeakers: orgWithColors,
        mappings: fetchedMappings?.mappings || {},
      });
    } catch (error) {
      logger.error('Error fetching speakers for story:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Failed to fetch speakers',
      );
    }
  }

  /**
   * Assign a Dato speaker to a transcript speaker label.
   */
  async assignSpeaker(
    storyId: string,
    speakerLabel: string,
    datoSpeakerId: string,
    _currentMappings?: Record<string, string>, // eslint-disable-line @typescript-eslint/no-unused-vars, no-unused-vars
  ): Promise<Result<void>> {
    try {
      // Always fetch fresh mappings to avoid read-modify-write race.
      clearSpeakerUtilsCache();
      const freshFetched = await fetchSpeakerVoiceMappings(storyId);
      if (!freshFetched?.mappings) {
        logger.error('assignSpeaker: failed to fetch fresh mappings');
        return err('EXTERNAL', 'Failed to fetch current speaker mappings');
      }
      const updatedMappings = {
        ...freshFetched.mappings,
        [speakerLabel]: datoSpeakerId,
      };
      const success = await updateSpeakerVoiceMappings(storyId, {
        mappings: updatedMappings,
        storyId,
        lastUpdated: Date.now(),
      });

      if (!success) {
        logger.error('assignSpeaker: write failed');
        return err('EXTERNAL', 'Failed to update speaker voice mappings');
      }
      // PLAT-9178: Auto-add org speaker to story if not already present
      try {
        await this.speakerRepository.addSpeakerReferenceToStory(
          datoSpeakerId,
          storyId,
        );
      } catch (addError) {
        logger.warn('Auto-add speaker to story failed', {
          datoSpeakerId,
          storyId,
          addError,
        });
      }
      this.invalidateCaches(storyId);
      return ok(undefined);
    } catch (error) {
      logger.error('Error assigning speaker:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Failed to assign speaker',
      );
    }
  }

  /**
   * Unassign a speaker from a transcript label (clear the mapping).
   */
  async unassignSpeaker(
    storyId: string,
    speakerLabel: string,
  ): Promise<Result<void>> {
    try {
      clearSpeakerUtilsCache();
      const fetched = await fetchSpeakerVoiceMappings(storyId);
      const currentMappings = fetched?.mappings || {};
      const updatedMappings = { ...currentMappings, [speakerLabel]: '' };

      const success = await updateSpeakerVoiceMappings(storyId, {
        mappings: updatedMappings,
        storyId,
        lastUpdated: Date.now(),
      });

      if (!success) {
        logger.error('unassignSpeaker: write failed');
        return err('EXTERNAL', 'Failed to update speaker voice mappings');
      }

      this.invalidateCaches(storyId);
      return ok(undefined);
    } catch (error) {
      logger.error('Error unassigning speaker:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Failed to unassign speaker',
      );
    }
  }

  /**
   * Update a speaker's metadata (name, title, bio, voice, headshots).
   */
  async updateSpeaker(
    speakerId: string,
    data: SpeakerData,
  ): Promise<Result<void>> {
    try {
      const repoData = toRepoSpeakerData(data);
      const updated = await this.speakerRepository.updateSpeaker(
        speakerId,
        repoData,
      );
      if (!updated) {
        logger.error(
          'Speaker update returned null — DatoCMS call may have failed',
          { speakerId },
        );
        return err('EXTERNAL', 'Speaker update failed in DatoCMS');
      }
      logger.info('Speaker updated successfully', {
        speakerId,
        updatedName: updated.name,
      });
      // Only clear the speakerUtils cache (not the useSpeakerLabels hook cache).
      // The hook cache is updated in-place by callers to avoid a DatoCMS CDN
      // re-fetch that returns stale data before propagation completes.
      clearSpeakerUtilsCache();
      return ok(undefined);
    } catch (error) {
      logger.error('Error updating speaker:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Failed to update speaker',
      );
    }
  }

  /**
   * Create a new speaker, add references to story/album, and assign to a transcript label.
   */
  async createAndAssignSpeaker(
    storyId: string,
    albumId: string | undefined | null,
    speakerLabel: string,
    data: SpeakerData,
    currentMappings: Record<string, string>,
  ): Promise<Result<DatoSpeaker>> {
    try {
      const repoData = toRepoSpeakerData(data);
      const newSpeaker = await this.speakerRepository.createSpeaker(
        data.name,
        data.title,
        data.bio,
        data.voice,
        { userId: undefined, storyId, albumId },
        repoData.headshots,
        data.gender || null,
      );

      if (!newSpeaker) {
        return err('EXTERNAL', 'Failed to create speaker');
      }

      await this.speakerRepository.addSpeakerReferenceToStory(
        newSpeaker.id,
        storyId,
      );
      if (albumId) {
        await this.speakerRepository.addSpeakerReferenceToAlbum(
          newSpeaker.id,
          albumId,
        );
      }

      if (speakerLabel) {
        const updatedMappings = {
          ...currentMappings,
          [speakerLabel]: newSpeaker.id,
        };
        const success = await updateSpeakerVoiceMappings(storyId, {
          mappings: updatedMappings,
          storyId,
          lastUpdated: Date.now(),
        });

        if (!success) {
          logger.warn('Speaker created but mapping update failed', {
            speakerId: newSpeaker.id,
            speakerLabel,
          });
        }
      }

      this.invalidateCaches(storyId);
      return ok(newSpeaker);
    } catch (error) {
      logger.error('Error creating and assigning speaker', {
        storyId,
        speakerLabel,
        ...extractErrorInfo(error),
      });
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Failed to create speaker',
      );
    }
  }

  /**
   * Load the organization's speaker library plus per-speaker story-usage
   * counts for the Speaker Manager, in a single round-trip.
   */
  async getOrgSpeakers(albumId: string): Promise<Result<OrgSpeakersResult>> {
    try {
      const [speakers, usage] = await Promise.all([
        this.speakerRepository.getAlbumSpeakers(albumId),
        this.speakerRepository.getSpeakerStoryUsage(albumId),
      ]);
      return ok({ speakers, usage });
    } catch (error) {
      logger.error('Error fetching org speakers:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Failed to fetch speakers',
      );
    }
  }

  /**
   * Create a speaker in the organization's library: create the item, add it to
   * `album.speakers[]`, then attach headshots. Unlike `createAndAssignSpeaker`,
   * there is no story reference or voice mapping.
   */
  async createOrgSpeaker(
    albumId: string,
    userId: string | null,
    data: SpeakerData,
  ): Promise<Result<DatoSpeaker>> {
    try {
      const created = await this.speakerRepository.createSpeaker(
        data.name,
        data.title,
        data.bio,
        data.voice,
        { userId, albumId },
        undefined,
        data.gender || null,
      );
      if (!created) {
        return err('EXTERNAL', 'Failed to create speaker');
      }
      // createSpeaker only sets `created_for_album` metadata; the speaker must
      // be added to `album.speakers[]` to appear in the org library.
      await this.speakerRepository.addSpeakerReferenceToAlbum(
        created.id,
        albumId,
      );
      if (data.headshots && data.headshots.length > 0) {
        const withHeadshots = await this.speakerRepository.updateSpeaker(
          created.id,
          { headshots: data.headshots.map(h => ({ upload_id: h.id })) },
        );
        if (!withHeadshots) {
          // The speaker exists in the library; only the photo failed to attach.
          // Don't fail the create (that would prompt a retry → duplicate).
          logger.warn('Speaker created but headshot attach failed', {
            speakerId: created.id,
          });
        }
      }
      return ok(created);
    } catch (error) {
      logger.error('Error creating org speaker:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Failed to create speaker',
      );
    }
  }

  /**
   * Remove a speaker from the organization's library (drops the album
   * reference only; story references are left intact).
   */
  async removeOrgSpeaker(
    albumId: string,
    speakerId: string,
  ): Promise<Result<void>> {
    try {
      await this.speakerRepository.removeSpeakerReferenceFromStoryOrAlbum(
        speakerId,
        albumId,
      );
      return ok(undefined);
    } catch (error) {
      logger.error('Error removing org speaker:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Failed to remove speaker',
      );
    }
  }

  /**
   * Find the Dato speaker assigned to a transcript label — a `spk_X` voice
   * mapping or a section's `override_<datoId>` label.
   */
  async findSpeakerForLabel(
    storyId: string,
    speakerLabel: string,
  ): Promise<Result<DatoSpeaker | null>> {
    try {
      const datoSpeakerId = speakerLabel.startsWith(OVERRIDE_LABEL_PREFIX)
        ? speakerLabel.slice(OVERRIDE_LABEL_PREFIX.length)
        : (await fetchSpeakerVoiceMappings(storyId))?.mappings?.[speakerLabel];
      if (!datoSpeakerId) return ok(null);

      const { storySpeakers, albumSpeakers } =
        await this.speakerRepository.getStorySpeakers(storyId);
      const allSpeakers = [...storySpeakers, ...albumSpeakers];
      const speaker = allSpeakers.find(s => s.id === datoSpeakerId);

      return ok(speaker || null);
    } catch (error) {
      logger.error('Error finding speaker for label:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Failed to find speaker',
      );
    }
  }

  private invalidateCaches(_storyId: string) {
    // Only clear the fetch caches (speakerUtils Maps).
    // Do NOT clear the label cache (speakerCache) — callers do optimistic
    // write-through updates and clearing would wipe them, causing labels
    // to flash back to anonymous until the CDN propagates.
    clearSpeakerUtilsCache();
  }
}

// ── Override management utilities ────────────────────────────────────

interface TranscriptionStore {
  getFinalTranscriptionElements(): TranscriptElement[];
  getOriginalTranscription(): { elements: TranscriptElement[] } | null;
  originalTranscription: { elements: TranscriptElement[] } | null;
}

/**
 * Find the indices of the whole PASSAGE assigned to one person by a section
 * override: every element carrying `override_<datoSpeakerId>` — words and
 * labelled punctuation alike (PLAT-11670) — widened with the marks that trail
 * each of those words in the same paragraph, so a mark left stale by the old
 * text-only writers moves together with its words on Unassign All / Reassign
 * All (see `withTrailingMarks`).
 *
 * The name says "passage", not "the elements carrying this label", because the
 * widening deliberately returns indices that do NOT carry the requested label: a
 * stale mark carries the PREVIOUS speaker's, and an unlabelled mark carries
 * none. Anyone who needs a genuine count of what a person currently owns must
 * filter by label themselves rather than use this.
 */
export function findOverridePassageIndices(
  elements: TranscriptElement[],
  datoSpeakerId: string,
): number[] {
  const overrideLabel = `override_${datoSpeakerId}`;
  const indices: number[] = [];
  for (let i = 0; i < elements.length; i++) {
    if (
      carriesSpeakerLabel(elements[i]) &&
      elements[i].speaker_label === overrideLabel
    ) {
      indices.push(i);
    }
  }
  return withTrailingMarks(elements, indices);
}

/**
 * Undo a section-level override: restore original speaker labels
 * and optionally remove the speaker from the story.
 */
export async function undoSectionOverride(
  datoSpeakerId: string,
  storyId: string,
  transcriptionStore: TranscriptionStore,
  speakerRepository?: SpeakerRepository,
): Promise<void> {
  const elements = transcriptionStore.getFinalTranscriptionElements();
  const indices = findOverridePassageIndices(elements, datoSpeakerId);

  if (indices.length > 0 && transcriptionStore.originalTranscription) {
    const restored = restoreRange(elements, indices);
    transcriptionStore.originalTranscription = {
      ...transcriptionStore.originalTranscription,
      elements: restored,
    };
  }

  if (speakerRepository) {
    try {
      await speakerRepository.removeSpeakerReferenceFromStoryOrAlbum(
        datoSpeakerId,
        storyId,
      );
    } catch (err) {
      logger.warn(
        'Failed to remove speaker from story after undo section reassign',
        err,
      );
    }
  }

  clearSpeakerCache(storyId);
}

/**
 * Replace all override_<datoId> labels with the voice-mapped spk_X label,
 * converting section overrides into full voice assignment.
 */
export function reassignOverridesToVoiceLabel(
  datoSpeakerId: string,
  voiceLabel: string,
  transcriptionStore: TranscriptionStore,
  storyId: string,
): void {
  const elements = transcriptionStore.getFinalTranscriptionElements();
  const overrideLabel = `override_${datoSpeakerId}`;

  if (!transcriptionStore.originalTranscription) return;

  // Words and labelled marks convert together (PLAT-11670). `convertRange`
  // repairs the passage's stale marks first so they carry the override label and
  // convert too, then clears the recorded previous speaker.
  const indices = findOverridePassageIndices(elements, datoSpeakerId);
  const updated = convertRange(elements, indices, overrideLabel, voiceLabel);

  transcriptionStore.originalTranscription = {
    ...transcriptionStore.originalTranscription,
    elements: updated,
  };

  clearSpeakerCache(storyId);
}
